/**
 * Single source of truth for user-facing eSIM status.
 *
 * Operator caught 2026-05-09: the dashboard card was showing THREE
 * different status labels for one eSIM — top-right pill said "Active"
 * (from DB enum), body STATUS said "Removed" (derived from RSP), and
 * the details drawer said "Paused" (i18n mapping of RSP=DISABLED).
 * All for an eSIM whose RSP status was DISABLED.
 *
 * This helper collapses that mess into one decision tree. Inputs:
 *  - `status`: DB enum ('active' | 'paused' | 'archived' | 'released' | 'reserved')
 *  - `platformStatus`: live RSP-side string ('ENABLED' | 'DISABLED' | 'INSTALLED' | 'RELEASED' | 'DELETED')
 *  - `installedAt`: when the user first scanned the QR
 *  - `lastSeenAt` / `lastUsageAt`: most recent traffic activity
 *
 * Output is a `{ key, label, severity }` triple consumed by both the
 * dashboard card and the details drawer. Whichever surface the user
 * looks at, the label is identical. Admin pages keep their own
 * separate "DB status" column — those are operator concepts the user
 * shouldn't see.
 */
import { t } from '../i18n';

export type EsimStatusKey =
  | 'reserved'    // payment pending, no eSIM yet
  | 'awaiting'    // issued, not yet installed (no installedAt)
  | 'online'      // installed + recent traffic (≤30 min)
  | 'installed'   // on the phone, idle
  | 'disabled'    // installed but turned off (RSP=DISABLED)
  | 'removed'     // released/deleted from the phone
  | 'archived';   // hidden by user — admin-only really

export type EsimStatusSeverity =
  | 'good'        // online — green
  | 'neutral'     // installed/awaiting — paper/blue
  | 'warning'     // disabled — orange
  | 'inactive';   // removed/archived — gray

export interface EsimStatus {
  key: EsimStatusKey;
  label: string;
  severity: EsimStatusSeverity;
}

interface StatusInput {
  status: 'active' | 'paused' | 'archived' | 'released' | 'reserved';
  platformStatus?: string | null;
  installedAt?: string | null;
  lastSeenAt?: string | null;
  lastUsageAt?: string | null;
  consumedMb?: number;
}

const RECENT_TRAFFIC_MS = 30 * 60 * 1000;

export function deriveEsimStatus(e: StatusInput): EsimStatus {
  // Reserved (payment-pending) takes priority — there's no actual eSIM yet.
  if (e.status === 'reserved') {
    return { key: 'reserved', label: t('dash.esims.statusReserved'), severity: 'neutral' };
  }
  // User archived it explicitly — admin-only really, but render gracefully.
  if (e.status === 'archived') {
    return { key: 'archived', label: t('dash.simStatus.statusArchived'), severity: 'inactive' };
  }

  const rsp = (e.platformStatus ?? '').toUpperCase();

  // RSP-DISABLED is the case operator hit: eSIM is installed on the phone
  // but turned off. From the user's perspective it doesn't carry data.
  // Render explicitly as "Disabled" — operator request 2026-05-09:
  // «сейчас этот есим выключен по факту и статус от RSP должен быть
  // таким — Статус: Disabled».
  if (rsp === 'DISABLED') {
    return { key: 'disabled', label: t('dash.simStatus.statusDisabled'), severity: 'warning' };
  }
  const neverUsed = !e.installedAt && (e.consumedMb ?? 0) === 0;
  // RELEASED is the SM-DP+ "ready to download" state (docs/integrations.md
  // §EsimPlatform): a freshly issued profile nobody has scanned yet. It
  // used to map straight to "Removed", so every new eSIM — the free trial
  // one included, seconds after it was claimed — told its owner it had been
  // removed before they ever installed it (found 2026-09-29). The platform
  // may report the same value once a used profile is deleted from the
  // phone, which is the case "Removed" was written for; `installedAt` is
  // stamped on first traffic and tells the two apart.
  if (rsp === 'DELETED' || (rsp === 'RELEASED' && !neverUsed)) {
    return { key: 'removed', label: t('dash.simStatus.statusRemoved'), severity: 'inactive' };
  }

  // RSP says active OR is unknown (not yet fetched / older list payload):
  // fall back to the local heuristic.
  const lastActivityIso = e.lastUsageAt ?? e.lastSeenAt ?? null;
  const recentMs = lastActivityIso ? Date.now() - new Date(lastActivityIso).getTime() : Infinity;

  if (neverUsed) {
    return { key: 'awaiting', label: t('dash.simStatus.statusAwaiting'), severity: 'neutral' };
  }
  if (recentMs < RECENT_TRAFFIC_MS) {
    return { key: 'online', label: t('dash.simStatus.statusOnline'), severity: 'good' };
  }
  return { key: 'installed', label: t('dash.simStatus.statusInstalled'), severity: 'neutral' };
}
